import { calculateInvoiceTotals, invoiceDataSchema, type InvoiceData, type InvoicePhotoMetadata } from "@shared/invoice";
import { PDFGenerator, type QuoteData } from "./pdf-generator";
import { InvoiceApi } from "./invoice-api";

export function validateInvoiceForPDF(data: InvoiceData): void {
  invoiceDataSchema.parse(data);
  if (!data.invoiceNumber.trim()) throw new Error("Bitte eine Rechnungsnummer eintragen.");
  if (!data.customerName.trim() || !data.customerAddress.trim()) {
    throw new Error("Bitte Rechnungsempfänger und Anschrift eintragen.");
  }
  if (!data.servicePeriod.trim()) throw new Error("Bitte den Leistungszeitraum eintragen.");
  const billable = data.items.filter((item) => item.billable);
  if (!billable.length || !billable.some((item) => item.quantity > 0)) {
    throw new Error("Bitte mindestens eine ausgeführte Leistung zum Abrechnen auswählen.");
  }
  if (billable.some((item) => !item.description.trim() || !item.unit.trim())) {
    throw new Error("Bitte Bezeichnung und Einheit aller abzurechnenden Positionen ausfüllen.");
  }
}

export function invoiceToPDFData(data: InvoiceData): QuoteData {
  return {
    documentType: "invoice",
    invoiceInfo: {
      dueDate: data.dueDate,
      servicePeriod: data.servicePeriod,
      sourceQuoteNumber: data.sourceQuoteNumber,
      sellerUid: data.sellerUid,
      customerUid: data.customerUid,
      paymentTerms: data.paymentTerms,
      coverPage: data.coverPage,
      groupByPart: data.groupByPart,
      partTitles: data.partTitles ? { ...data.partTitles } : undefined,
    },
    taxRate: data.taxRate,
    projectName: data.projectName,
    customerName: data.customerName,
    customerAddress: data.customerAddress,
    quoteNumber: data.invoiceNumber,
    quoteDate: data.invoiceDate,
    validUntil: data.dueDate,
    items: data.items.filter((item) => item.billable).map((item) => ({
      description: item.description,
      quantity: item.quantity,
      unit: item.unit,
      unitPrice: item.unitPrice,
      total: Math.round((item.quantity * item.unitPrice + Number.EPSILON) * 100) / 100,
      sourceLabel: item.sourceLabel,
      teil: item.teil,
    })),
    notes: data.notes,
    ...calculateInvoiceTotals(data),
  };
}

export async function generateInvoicePDF(
  data: InvoiceData, photos: InvoicePhotoMetadata[], invoiceId: number | string,
  options?: { preview?: boolean },
): Promise<Blob> {
  if (options?.preview) invoiceDataSchema.parse(data);
  else validateInvoiceForPDF(data);
  const pdfData = invoiceToPDFData(data);
  pdfData.isPreview = !!options?.preview;
  // Read all images explicitly; never silently print a blank photo on failures.
  pdfData.photos = await Promise.all(photos.map(async (photo) => {
    const response = await fetch(InvoiceApi.photoUrl(invoiceId, photo.id), { credentials: "include" });
    if (!response.ok) throw new Error(`Rechnungsfoto „${photo.fileName}“ konnte nicht geladen werden.`);
    const blob = await response.blob();
    const preview = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error("Rechnungsfoto konnte nicht gelesen werden."));
      reader.readAsDataURL(blob);
    });
    return {
      preview, file: new File([blob], photo.fileName, { type: photo.mimeType }),
      displayName: photo.displayName || photo.fileName,
      description: photo.description,
    };
  }));
  return new PDFGenerator().generateQuotePDF(pdfData);
}
